import { ReactNode } from 'react';

import { decode } from 'he';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import breaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import markdown from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';

import { chipClassName } from '@/hooks/ContextVariable';

/**
 * Regex pattern for matching context variables like {{@variable}} or {{$variable}}
 */
export const HANDLE_REGEX = /\{{(@|\$)([^}@${^ ]+)\}}/g;

/**
 * Renders text with context variables styled as blue chips
 * @param text - Text containing context variables like {{$variable}}
 * @returns Array of ReactNodes with styled variables
 */
export const renderTextWithVariables = (text: string): ReactNode[] => {
  const result: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Create a new regex instance to avoid shared state issues
  const regex = new RegExp(HANDLE_REGEX.source, HANDLE_REGEX.flags);

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    // Add the styled variable using the shared chipClassName
    result.push(
      <span key={match.index} className={chipClassName}>
        {match[0]}
      </span>
    );

    lastIndex = regex.lastIndex;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
};

/**
 * Escapes standalone ordered-list markers (e.g. "151." or "2)") so they render
 * as literal text instead of producing an empty list item.
 *
 * CommonMark treats a number followed by `.` or `)` at the start of a line as
 * an ordered-list marker. When that marker has no content after it (common in
 * voice transcriptions and short numeric user replies), the line becomes an
 * empty `<li>` and the visible content disappears. We only touch lines whose
 * entire content is a lone marker — real lists like `1. First\n2. Second`
 * still render as lists.
 */
export const escapeStandaloneOrderedListMarkers = (text: string): string => {
  if (!text) {
    return text;
  }
  return text.replace(/^([ \t]{0,3})(\d{1,9})([.)])([ \t]*)$/gm, '$1$2\\$3$4');
};

/**
 * Custom plugin to add https protocol to links without protocol
 */
const remarkAddHttpsProtocol = () => {
  return (tree) => {
    visit(tree, 'link', (node) => {
      const url = node.url;
      if (typeof url === 'string' && !/^(https?:)?\/\//.test(url) && !url.startsWith('/')) {
        node.url = 'https://' + url;
      }
    });
  };
};

/**
 * Custom rehype plugin to add 'markdown' class to all HTML elements
 */
const rehypeAddMarkdownClass = () => {
  return (tree) => {
    visit(tree, 'element', (node) => {
      node.properties = node.properties || {};
      const existingClassName = node.properties.className;

      if (Array.isArray(existingClassName)) {
        // className is already an array, append if not present
        if (!existingClassName.includes('markdown')) {
          node.properties.className = [...existingClassName, 'markdown'];
        }
      } else if (typeof existingClassName === 'string') {
        // className is a string, convert to array
        node.properties.className = [existingClassName, 'markdown'];
      } else {
        // No existing className, set to 'markdown'
        node.properties.className = 'markdown';
      }
    });
  };
};

// Every plugin in this pipeline is synchronous, so the processor can run either
// asynchronously (`process`) or synchronously (`processSync`). It's built once and
// reused across calls — a unified processor is reusable once frozen, and rebuilding
// the plugin chain per message would be wasted work on the render hot path.
const markdownProcessor = unified()
  .use(markdown)
  .use(remarkGfm)
  .use(breaks)
  .use(remarkAddHttpsProtocol)
  .use(remarkRehype)
  .use(rehypeSanitize)
  .use(rehypeAddMarkdownClass)
  .use(rehypeExternalLinks, { target: '_blank', rel: ['noreferrer', 'noopener'] })
  .use(rehypeStringify);

/**
 * Converts markdown text to HTML string with sanitization and link handling
 * @param markdownText - Markdown formatted string
 * @returns Promise resolving to HTML string
 */
export const markdownToHtml = async (markdownText: string): Promise<string> => {
  if (typeof markdownText === 'undefined' || markdownText === null) {
    return '';
  }

  try {
    const decodedMarkdown = decode(markdownText);
    const result = await markdownProcessor.process(decodedMarkdown);
    return String(result);
  } catch (err) {
    console.error('Error converting markdown to HTML:', err);
    throw err;
  }
};

/**
 * Synchronous version of {@link markdownToHtml}. Use this when the HTML must be
 * available on the first render — e.g. chat message bubbles — so the content
 * doesn't render empty and then grow a tick later (which yanks scroll position).
 * @param markdownText - Markdown formatted string
 * @returns HTML string
 */
export const markdownToHtmlSync = (markdownText: string): string => {
  if (typeof markdownText === 'undefined' || markdownText === null) {
    return '';
  }

  const decodedMarkdown = decode(markdownText);
  const result = markdownProcessor.processSync(decodedMarkdown);
  return String(result);
};
